import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {BicButtonComponent, BicInputComponent, BicPopupComponent,} from '@buildinginformationcloud/bic-ui-components';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faPlus, faXmark} from '@fortawesome/pro-solid-svg-icons';

interface OptionItem {
  value: string;
}

/**
 * Bearbeitet eine Liste von Auswahlmöglichkeiten (`string[]`).
 *
 * Bewusst ohne Bezug zum bearbeiteten Objekt: derselbe Dialog pflegt die Optionen eines Tags, eines
 * Ausgabeparameters, eines Prüfparameters und eines Eingabeparameters. Früher lag er unter
 * `ki-editor/tag-options-popup` und hieß nach seinem ersten Aufrufer — bei fünf Aufrufern war das
 * ein Name, der die Sache verfehlt.
 */
@Component({
  selector: 'app-options-popup',
  standalone: true,
  imports: [BicPopupComponent, BicButtonComponent, BicInputComponent, FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './options-popup.component.html',
})
export class OptionsPopupComponent implements AfterViewChecked {
  /** Name des bearbeiteten Objekts – erscheint im Dialogtitel, damit klar ist, WESSEN Optionen. */
  @Input() label = '';
  @ViewChildren('optionInputHost', {read: ElementRef}) private optionInputHosts!: QueryList<ElementRef<HTMLElement>>;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<string[]>();

  optionItems: OptionItem[] = [];
  private pendingFocusIndex: number | null = null;

  @Input() set initialOptions(value: string[]) {
    this.optionItems = value?.length ? value.map(v => ({value: v})) : [{value: ''}];
    this.pendingFocusIndex = 0;
  }

  protected readonly faPlus = faPlus;
  protected readonly faXmark = faXmark;

  addOption(): void {
    this.optionItems = [...this.optionItems, {value: ''}];
    this.pendingFocusIndex = this.optionItems.length - 1;
  }

  onOptionEnter(index: number, event: Event): void {
    event.preventDefault();
    if (index === this.optionItems.length - 1) {
      this.addOption();
    } else {
      this.focusInput(index + 1);
    }
  }

  removeOption(index: number): void {
    const nextItems = this.optionItems.filter((_, i) => i !== index);
    this.optionItems = nextItems.length ? nextItems : [{value: ''}];
    if (!nextItems.length) {
      this.pendingFocusIndex = 0;
    }
  }

  onCancel(): void {
    this.closed.emit();
  }

  onSave(): void {
    this.saved.emit(this.optionItems.map(o => o.value).filter(v => v.trim() !== ''));
  }

  ngAfterViewChecked(): void {
    if (this.pendingFocusIndex === null) return;
    this.focusInput(this.pendingFocusIndex);
    this.pendingFocusIndex = null;
  }

  private focusInput(index: number): void {
    const host = this.optionInputHosts.get(index)?.nativeElement;
    if (!host) return;

    const shadowRoot = (host as any).shadowRoot as ShadowRoot | undefined;
    const input = (shadowRoot?.querySelector('input') || host.querySelector('input')) as HTMLInputElement | null;
    if (!input) return;

    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
  }
}
